/**
 * Step 4 - Two-column transfer list. Include (left) vs Skip (right).
 * Rows show the detected category + suggested client. User can:
 *   - tick rows on either side and move them with the arrow buttons
 *   - change the assigned client per row
 *   - bulk-move all selected
 *   - commit (validates that every "include" row has a client)
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft, ArrowRight, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { importService, CATEGORY_LABELS, ImportItem } from '@/services/api/import';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';

type Effective = 'include' | 'skip';

function effectiveAction(item: ImportItem): Effective {
  if (item.userAction === 'include') return 'include';
  if (item.userAction === 'skip') return 'skip';
  // default - treat 'review' as 'include' so user can see it on the left
  return item.defaultAction === 'skip' ? 'skip' : 'include';
}

export default function Step4Review({
  sessionId,
  onCommit,
  onCancel,
}: {
  sessionId: string;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data } = useQuery({
    queryKey: ['import-items', sessionId],
    queryFn: () => importService.getItems(sessionId),
    refetchInterval: false,
  });

  const { data: clients } = useQuery({
    queryKey: ['clients-for-import'],
    queryFn: async () => {
      const r = await apiClient.get('/clients?limit=500');
      const arr = r.data.data?.clients || r.data.data || [];
      return arr as Array<{ id: string; displayName?: string; display_name?: string }>;
    },
  });

  const items = data?.items || [];

  const left = useMemo(() => items.filter(i => effectiveAction(i) === 'include'), [items]);
  const right = useMemo(() => items.filter(i => effectiveAction(i) === 'skip'), [items]);

  const patchMut = useMutation({
    mutationFn: async ({ itemId, patch }: { itemId: string; patch: any }) => {
      await importService.patchItem(sessionId, itemId, patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['import-items', sessionId] }),
  });

  const bulkMut = useMutation({
    mutationFn: async ({ itemIds, patch }: { itemIds: string[]; patch: any }) => {
      await importService.patchItemsBulk(sessionId, itemIds, patch);
    },
    onSuccess: () => { setSelected({}); qc.invalidateQueries({ queryKey: ['import-items', sessionId] }); },
  });

  const commitMut = useMutation({
    mutationFn: async () => importService.commit(sessionId),
    onSuccess: () => onCommit(),
    onError: (e: any) => alert(e?.response?.data?.error || e?.message || 'Commit failed'),
  });

  const toggleAll = (rows: ImportItem[], on: boolean) => {
    const next = { ...selected };
    for (const r of rows) next[r.id] = on;
    setSelected(next);
  };

  const moveSelected = (action: Effective) => {
    const ids = Object.entries(selected).filter(([, on]) => on).map(([id]) => id);
    if (ids.length === 0) return;
    bulkMut.mutate({ itemIds: ids, patch: { userAction: action } });
  };

  const moveAll = (from: Effective, to: Effective) => {
    const rows = from === 'include' ? left : right;
    bulkMut.mutate({ itemIds: rows.map(r => r.id), patch: { userAction: to } });
  };

  // Default-resolve client: client_override → suggested → null
  const resolvedClientId = (i: ImportItem) =>
    i.clientOverride?.id || i.suggestedClient?.id || data?.session?.target_client_id || null;

  const unresolvedIncludeCount = left.filter(i => !resolvedClientId(i)).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-3">
        {/* LEFT: INCLUDE */}
        <Pane
          title="Include"
          subtitle={`${left.length} file${left.length === 1 ? '' : 's'} will be imported`}
          variant="include"
        >
          <PaneHeader
            count={left.length}
            allSelected={left.length > 0 && left.every(i => selected[i.id])}
            onToggleAll={(on) => toggleAll(left, on)}
            onMoveAll={() => moveAll('include', 'skip')}
          />
          <ScrollArea className="h-[480px]">
            <div className="divide-y">
              {left.length === 0 ? <EmptyPane label="No files in this column" />
                : left.map(item => (
                  <Row
                    key={item.id}
                    item={item}
                    clients={clients || []}
                    selected={!!selected[item.id]}
                    onSelect={(v) => setSelected(prev => ({ ...prev, [item.id]: v }))}
                    onMove={() => patchMut.mutate({ itemId: item.id, patch: { userAction: 'skip' } })}
                    moveLabel="Skip"
                    onClientChange={(clientId) => patchMut.mutate({ itemId: item.id, patch: { clientId } })}
                    onCategoryChange={(categoryOverride) => patchMut.mutate({ itemId: item.id, patch: { categoryOverride } })}
                    resolvedClientId={resolvedClientId(item)}
                  />
                ))
              }
            </div>
          </ScrollArea>
        </Pane>

        {/* MIDDLE: move-selected controls */}
        <div className="flex lg:flex-col items-center justify-center gap-2 lg:gap-3 lg:px-2">
          <Button variant="outline" size="icon" title="Move selected to Skip" onClick={() => moveSelected('skip')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" title="Move ALL to Skip" onClick={() => moveAll('include', 'skip')}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" title="Move selected to Include" onClick={() => moveSelected('include')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" title="Move ALL to Include" onClick={() => moveAll('skip', 'include')}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        </div>

        {/* RIGHT: SKIP */}
        <Pane
          title="Skip"
          subtitle={`${right.length} file${right.length === 1 ? '' : 's'} will NOT be imported`}
          variant="skip"
        >
          <PaneHeader
            count={right.length}
            allSelected={right.length > 0 && right.every(i => selected[i.id])}
            onToggleAll={(on) => toggleAll(right, on)}
            onMoveAll={() => moveAll('skip', 'include')}
            moveAllLabel="Include all"
          />
          <ScrollArea className="h-[480px]">
            <div className="divide-y">
              {right.length === 0 ? <EmptyPane label="No files in this column" />
                : right.map(item => (
                  <Row
                    key={item.id}
                    item={item}
                    clients={clients || []}
                    selected={!!selected[item.id]}
                    onSelect={(v) => setSelected(prev => ({ ...prev, [item.id]: v }))}
                    onMove={() => patchMut.mutate({ itemId: item.id, patch: { userAction: 'include' } })}
                    moveLabel="Include"
                    onClientChange={(clientId) => patchMut.mutate({ itemId: item.id, patch: { clientId } })}
                    onCategoryChange={(categoryOverride) => patchMut.mutate({ itemId: item.id, patch: { categoryOverride } })}
                    resolvedClientId={resolvedClientId(item)}
                    dimmed
                  />
                ))
              }
            </div>
          </ScrollArea>
        </Pane>
      </div>

      <div className="flex items-center justify-between border-t pt-4">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <div className="flex items-center gap-3">
          {unresolvedIncludeCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              {unresolvedIncludeCount} file{unresolvedIncludeCount === 1 ? ' needs' : 's need'} a client
            </span>
          )}
          <Button
            onClick={() => commitMut.mutate()}
            disabled={commitMut.isPending || left.length === 0 || unresolvedIncludeCount > 0}
            className="gap-2"
          >
            {commitMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Import {left.length} file{left.length === 1 ? '' : 's'} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Pane({ title, subtitle, variant, children }: {
  title: string; subtitle: string; variant: 'include' | 'skip'; children: React.ReactNode;
}) {
  return (
    <Card className={cn(variant === 'skip' && 'bg-muted/30')}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>{title}</span>
          <span className="text-[11px] font-normal text-muted-foreground">{subtitle}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function PaneHeader({ count, allSelected, onToggleAll, onMoveAll, moveAllLabel }:
  { count: number; allSelected: boolean; onToggleAll: (v: boolean) => void; onMoveAll: () => void; moveAllLabel?: string }) {
  return (
    <div className="flex items-center justify-between border-b px-3 py-2 bg-muted/20 text-xs">
      <label className="inline-flex items-center gap-2">
        <Checkbox checked={allSelected} onCheckedChange={(v) => onToggleAll(!!v)} disabled={count === 0} />
        <span className="text-muted-foreground">Select all</span>
      </label>
      <button onClick={onMoveAll} disabled={count === 0} className="text-primary hover:underline disabled:opacity-50">
        {moveAllLabel || `Move all (${count})`}
      </button>
    </div>
  );
}

function EmptyPane({ label }: { label: string }) {
  return <div className="py-12 text-center text-xs text-muted-foreground">{label}</div>;
}

function Row({
  item, clients, selected, onSelect, onMove, moveLabel, onClientChange, onCategoryChange, resolvedClientId, dimmed,
}: {
  item: ImportItem;
  clients: Array<{ id: string; displayName?: string; display_name?: string }>;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onMove: () => void;
  moveLabel: string;
  onClientChange: (id: string) => void;
  onCategoryChange: (cat: string) => void;
  resolvedClientId: string | null;
  dimmed?: boolean;
}) {
  const isLowConf = item.detectionConfidence !== null && item.detectionConfidence < 0.7;
  const catLabel = CATEGORY_LABELS[item.categoryOverride || item.detectedCategory || 'unknown'] || 'Unknown';

  return (
    <div className={cn('px-3 py-2.5 flex items-start gap-2.5 hover:bg-muted/30 transition-colors', dimmed && 'opacity-70')}>
      <Checkbox checked={selected} onCheckedChange={(v) => onSelect(!!v)} className="mt-1" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2">
          <p className="text-sm truncate font-medium" title={item.filename}>{item.filename}</p>
          {isLowConf && (
            <span title={`Low confidence (${Math.round((item.detectionConfidence || 0) * 100)}%)`}>
              <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] py-0">{catLabel}</Badge>
          {item.fileSizeBytes ? <span className="text-[10px] text-muted-foreground">{formatBytes(item.fileSizeBytes)}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <Select value={resolvedClientId || ''} onValueChange={onClientChange}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue placeholder="Choose client…" />
            </SelectTrigger>
            <SelectContent>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-xs">
                  {c.displayName || c.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={item.categoryOverride || item.detectedCategory || ''} onValueChange={onCategoryChange}>
            <SelectTrigger className="h-7 text-xs w-[140px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1" onClick={onMove}>
        {moveLabel}
        {moveLabel === 'Skip' ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
